import { useEffect } from 'react'
import { GetServerSidePropsContext } from 'next'
import { parse as cookieParse } from 'cookie'
import safeJsonStringify from 'safe-json-stringify'
import merge from 'deepmerge'

import { getLocaleFromCookies } from '../../../../siteBuilder/locale/getLocaleFromCookies'
import { getContentfulLocale } from '../../../../siteBuilder/locale/getContentfulLocale'
import { getBlogFromSlug } from '../../../../blogBuilder/utils/getBlogFromSlug'
import sitebuilderConfig from '../../../../../sitebuilder.config'
import { Locale, removeLocaleFromSlug } from '../../../../siteBuilder/locale'
import { getHost } from '../../../../utils/getHost'

import alliumTheme from '@telus-uds/theme-allium'
import { BaseProvider } from '@telus-uds/components-web'
import { getFullBlogUrls } from 'src/blogBuilder/utils/blogUrl'
import { buildBlogBreadcrumbs } from '../../../../blogBuilder/utils/breadcrumbSlugs'
import themes from '../../../../blogBuilder/theme/Blog'
import { GenericBlogTemplate } from '../../../../blogBuilder/utils/types/theme'
import { BlogUrl } from '../../../../blogBuilder/utils/blogUrl'
import { ANALYTICS_PARAMETER_TYPE_BLOG } from '../../../../blogBuilder/utils/constants'
import { getLineOfBusiness } from '../../../../siteBuilder/lineOfBusiness/lineOfBusiness'
import { adobePageAnalytics } from '../../../../siteBuilder/utils/adobeAnalyticsObject'
import { pageAnalytics } from '../../../../siteBuilder/utils/analyticsObject'
import { Viewport } from '../../../../siteBuilder/components/Theme/Theme'

// Function to detect viewport from User-Agent (server-side estimation)
const getInitialViewportFromUserAgent = (userAgent?: string): Viewport => {
  if (!userAgent) return 'lg' // Default fallback

  // Mobile devices - typically smaller screens
  if (/Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return 'xs'
  }

  // Tablet devices - medium screens
  if (/iPad|Tablet/i.test(userAgent)) {
    return 'md'
  }

  // Desktop - assume large screen by default
  return 'xl'
}

const getBlogTemplate = async (slug: string, locale: string, transactionId: string) => {
  const blogLocale = `${locale.split('-')[0]}-${locale.split('-')[1]}`
  try {
    const results = await getBlogFromSlug(slug === '' ? '/' : slug, sitebuilderConfig.blog, blogLocale, transactionId)
    return results
  } catch (error) {
    return null
  }
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const { params } = ctx

  ctx.res?.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59')

  const slug = removeLocaleFromSlug(params?.blog)
  const transactionId = (ctx.res?.getHeader('sb2-transaction-id') as string) || ''

  const serializedCookies = ctx.res?.getHeader('Set-Cookie')
  const cookies = Array.isArray(serializedCookies) ? cookieParse(serializedCookies.join('; ')) : {}

  // const reqSetCookieHeader = ctx.req.headers['cookie']
  // const resSetCookieHeader = ctx.res?.getHeader('Set-Cookie')
  // // nextjs 13 cookie parsing
  // if (reqSetCookieHeader) {
  //   cookies = cookieParse(reqSetCookieHeader)
  // } else if (resSetCookieHeader) {
  //   // next 12 cookie parsing
  //   if (Array.isArray(resSetCookieHeader)) {
  //     cookies = cookieParse(resSetCookieHeader.join(';'))
  //   } else {
  //     cookies = cookieParse(resSetCookieHeader.toString())
  //   }
  // }

  const locale = getLocaleFromCookies(cookies)
  const contentfulLocale = getContentfulLocale(locale)

  const blogTemplate = await getBlogTemplate(slug, contentfulLocale, transactionId)
  if (!blogTemplate || !blogTemplate.fields) {
    return {
      notFound: true,
    }
  }

  const fullBlogUrls = getFullBlogUrls(ctx.req, slug, locale.language, getHost())

  if (blogTemplate) {
    blogTemplate.fields?.categories.forEach((category) => {
      if (category?.fields?.articles) {
        category.fields.articles.forEach((d) => {
          if (d.fields) {
            delete d.fields.body
          }
        })

        // TODO: look into graphql for more optimal fetching
        category.fields.articles = category.fields.articles
          .filter((d) => d.fields)
          .map((d) => {
            return {
              sys: { id: d?.sys?.id, type: d?.sys?.type },
              fields: {
                publishDate: d?.fields?.publishDate,
                languageSupport: d?.fields?.languageSupport,
                articleTags: d.fields.articleTags,
              },
            }
          }) as any
      }
    })
  }

  // fixes circular reference error: https://github.com/vercel/next.js/discussions/10992
  const stringifiedData: string = safeJsonStringify(blogTemplate.fields)
  const data = JSON.parse(stringifiedData)

  const pageUrl = ctx?.req?.url
  const analyticsParams = {
    pageUrl,
    prov: locale.province,
    lang: locale.language,
    type: ANALYTICS_PARAMETER_TYPE_BLOG,
  }

  // Detect initial viewport from User-Agent
  const userAgent = ctx.req.headers['user-agent']
  const initialViewport = getInitialViewportFromUserAgent(userAgent)

  return {
    props: {
      analyticsParams,
      locale,
      genericBlogTemplate: data,
      fullBlogUrls,
      analyticsScripts: getLineOfBusiness(slug).analyticsScripts,
      brand: 'telus',
      initialViewport,
    },
  }
}

type Props = {
  analyticsParams: {
    pageUrl: string
    prov: string
    lang: string
    type: string
  }
  genericBlogTemplate: GenericBlogTemplate
  locale: Locale
  fullBlogUrls: BlogUrl
  initialViewport?: Viewport
}

type BlogType = (Props: Props) => JSX.Element

const Blog: BlogType = ({ genericBlogTemplate, locale, fullBlogUrls, initialViewport }) => {
  const { slug } = genericBlogTemplate || {}
  const { province, language } = locale

  const analyticsGenericBlogTemplate = { ...genericBlogTemplate, slug: `blog/${genericBlogTemplate.slug}` }

  useEffect(() => {
    const defaultDataLayerConfig = {
      page: {
        name: `blog/${slug}`,
        province: province,
        language: language,
      },
      event: {},
      user: {},
    }
    // Initializing window.dataLayer - will eventually be deprecated
    const dataLayerConfig = pageAnalytics(analyticsGenericBlogTemplate)
    window.dataLayer = merge(defaultDataLayerConfig, dataLayerConfig)
    // This is needed as adobePageAnalytics hecks if the invalid region landing page is used on generic page template
    const incorrectRegion = false
    adobePageAnalytics(analyticsGenericBlogTemplate, locale, incorrectRegion)
  }, [])

  if (!genericBlogTemplate || !genericBlogTemplate?.categories) return null

  const BlogTheme = themes[genericBlogTemplate.theme] || themes.default
  const breadcrumbs = buildBlogBreadcrumbs(genericBlogTemplate, fullBlogUrls)

  return (
    <BaseProvider
      defaultTheme={alliumTheme}
      themeOptions={{
        //@ts-ignore
        defaultViewport: initialViewport || 'xl',
      }}
    >
      <BlogTheme
        genericBlogTemplate={genericBlogTemplate}
        locale={locale}
        fullBlogUrls={fullBlogUrls}
        breadcrumbs={breadcrumbs as any}
      />
    </BaseProvider>
  )
}

export default Blog
